import { useMemo } from 'react';

import { Practitioner } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { Bean } from '@dreryk/react-common';
import {
   AlignItems,
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   HorizontalAlign,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   JustifyContent,
   ValueRendererProps,
} from '@dreryk/react-components';

import { medicalServiceTranslationKeys } from '../../../../translations';
import { MedicalServiceFormController } from '../../form/medicalServiceForm.types';
import { MedicalServicePractitionerBean } from '../practitioner.types';

import { MedicalServicePractitionerDisplayNameCell } from './cells/MedicalServicePractitionerDisplayNameCell';
import { MedicalServicePractitionerIndividualPriceCell } from './cells/MedicalServicePractitionerIndividualPriceCell';
import { MedicalServicePractitionerRemoveConfirmationEventInterceptor } from './cells/MedicalServicePractitionerRemoveConfirmationEventInterceptor';
import { MedicalServicePractitionerSalaryCell } from './cells/MedicalServicePractitionerSalaryCell';
import { MedicalServicePractitionerSettlementTypeCell } from './cells/MedicalServicePractitionerSettlementTypeCell';
import { MedicalServicePractitionerSpecialtiesCell } from './cells/MedicalServicePractitionerSpecialtiesCell';
import { MedicalServicePractitionerTaxRateCell } from './cells/MedicalServicePractitionerTaxRateCell';
import {
   MedicalServicePractitionerUsingDefaultConfigurationCell,
   MedicalServicePractitionerUsingDefaultConfigurationCellHeader,
} from './cells/MedicalServicePractitionerUsingDefaultConfigurationCell';
import { MedicalServicePractitionerInteractiveTableSelector } from './MedicalServicePractitionerInteractiveTableSelector';

type MedicalServicePractitionerInteractiveTableColumnConfigurationOptions = Pick<
   MedicalServiceFormController,
   'control'
> & { medicalServiceName?: string; removeWithConfirmation?: boolean };

// eslint-disable-next-line max-lines-per-function
const useColumnsConfiguration = (
   options: MedicalServicePractitionerInteractiveTableColumnConfigurationOptions
): InteractiveTableColumnConfiguration<MedicalServicePractitionerBean>[] => {
   const { control, medicalServiceName, removeWithConfirmation } = options;

   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnWidths = createDynamicColumnsWidth({
         id: '0px',
         displayName: '50%',
         specialties: '50%',
         individualPrice: '110px',
         taxRate: '46px',
         settlementType: '120px',
         salary: '110px',
         usingDefaultConfiguration: '40px',
         options: '24px',
      });

      return [
         {
            name: 'id',
            width: columnWidths.id,
            renderer: ({ bean, index }: ValueRendererProps<MedicalServicePractitionerBean>) => {
               return (
                  <InteractiveTable.ControllerCell testId={'id'} name={'id'} index={index} defaultValue={bean.id} />
               );
            },
            hidden: true,
         },
         {
            name: 'displayName',
            label: medicalServiceTranslationKeys.shared.practitioner.table.headers.displayName,
            width: columnWidths.displayName,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            renderer: ({ bean, index }: ValueRendererProps<MedicalServicePractitionerBean>) => {
               return (
                  <MedicalServicePractitionerDisplayNameCell
                     testId={'displayName'}
                     name={'displayName'}
                     index={index}
                     defaultValue={bean.displayName}
                  />
               );
            },
         },
         {
            name: 'specialties',
            width: columnWidths.specialties,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            label: medicalServiceTranslationKeys.shared.practitioner.table.headers.specialties,
            renderer: ({ bean, index }: ValueRendererProps<MedicalServicePractitionerBean>) => {
               return (
                  <MedicalServicePractitionerSpecialtiesCell
                     testId={'specialties'}
                     name={'specialties'}
                     index={index}
                     defaultValue={bean.specialties}
                  />
               );
            },
         },
         {
            name: 'individualPrice',
            width: columnWidths.individualPrice,
            label: medicalServiceTranslationKeys.shared.practitioner.table.headers.individualPrice,
            justifyContent: JustifyContent.END,
            textAlign: HorizontalAlign.RIGHT,
            renderer: ({ bean, index }: ValueRendererProps<MedicalServicePractitionerBean>) => {
               return (
                  <MedicalServicePractitionerIndividualPriceCell
                     name={'individualPrice'}
                     testId={'individualPrice'}
                     index={index}
                     defaultValue={bean.individualPrice}
                  />
               );
            },
         },
         {
            name: 'taxRate',
            label: medicalServiceTranslationKeys.shared.practitioner.table.headers.taxRate,
            width: columnWidths.taxRate,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,
            renderer: ({ index, bean }) => {
               return (
                  <MedicalServicePractitionerTaxRateCell
                     name={'taxRate'}
                     testId={'taxRate'}
                     index={index}
                     defaultValue={bean.taxRate}
                  />
               );
            },
         },
         {
            name: 'settlementType',
            label: medicalServiceTranslationKeys.shared.practitioner.table.headers.settlementType,
            width: columnWidths.settlementType,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            renderer: ({ index, bean }) => {
               return (
                  <MedicalServicePractitionerSettlementTypeCell
                     testId={'settlementType'}
                     name={'settlementType'}
                     index={index}
                     defaultValue={bean.settlementType}
                  />
               );
            },
         },
         {
            name: 'salary',
            width: columnWidths.salary,
            label: medicalServiceTranslationKeys.shared.practitioner.table.headers.salary,
            justifyContent: JustifyContent.END,
            textAlign: HorizontalAlign.RIGHT,
            renderer: ({ bean, index }: ValueRendererProps<MedicalServicePractitionerBean>) => {
               return (
                  <MedicalServicePractitionerSalaryCell
                     index={index}
                     testId={'salary'}
                     defaultType={bean.settlementType}
                     defaultFixedValue={bean.fixedValue ?? null}
                     defaultProportionalValue={bean.proportionalValue ?? null}
                  />
               );
            },
         },
         {
            name: 'usingDefaultConfiguration',
            width: columnWidths.usingDefaultConfiguration,
            headerRenderer: MedicalServicePractitionerUsingDefaultConfigurationCellHeader,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,
            renderer: ({ index, bean }) => {
               return (
                  <MedicalServicePractitionerUsingDefaultConfigurationCell
                     testId={'usingDefaultConfiguration'}
                     name={'usingDefaultConfiguration'}
                     index={index}
                     defaultValue={bean.usingDefaultConfiguration}
                     control={control}
                  />
               );
            },
         },
         {
            name: 'options',
            width: columnWidths.options,
            renderer: ({ index, bean: { displayName } }) => (
               <MedicalServicePractitionerRemoveConfirmationEventInterceptor
                  medicalServiceName={medicalServiceName}
                  practitionerName={displayName}
                  shouldShowModal={removeWithConfirmation}
               >
                  <InteractiveTable.DeleteRowCell index={index} />
               </MedicalServicePractitionerRemoveConfirmationEventInterceptor>
            ),
         },
      ];
   }, [control, medicalServiceName, removeWithConfirmation]);
};

type MedicalServicePractitionerInteractiveTableProps = Pick<MedicalServiceFormController, 'register' | 'control'> & {
   readonly practitioners: Practitioner[];
   readonly medicalServiceName?: string;
   readonly removeWithConfirmation?: boolean;
};

export const MedicalServicePractitionerInteractiveTable = (
   props: MedicalServicePractitionerInteractiveTableProps
): JSX.Element => {
   const columns = useColumnsConfiguration({
      control: props.control,
      medicalServiceName: props.medicalServiceName,
      removeWithConfirmation: props.removeWithConfirmation,
   });

   return (
      <InteractiveTable<MedicalServicePractitionerBean, Bean>
         name={'practitioners'}
         register={props.register}
         columns={columns}
         testId={'practitioners'}
         header={
            <MedicalServicePractitionerInteractiveTableSelector
               practitioners={props.practitioners}
               register={props.register}
               control={props.control}
            />
         }
         placeholder={
            <EmptyStatePlaceholder
               heading={medicalServiceTranslationKeys.shared.practitioner.table.placeholder.heading}
               testId={'noPractitioners'}
            />
         }
      />
   );
};
